import clsx from 'clsx'
import { StyledSvgIcon, setSvgIconSize } from 'LuxKit/SvgIcon'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import styled, { useTheme } from 'styled-components'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useSiteTakeoverCampaignUIState } from '../hooks/useSiteTakeoverCampaignUIState'
import ModalContext from 'contexts/ModalContext'
import LunarSiteTakeoverDetailsModal from 'takeovers/lunarNewYearTakeover/components/LunarSiteTakeoverDetailsModal'
import { LUNAR_NEW_YEAR_THEME_ID } from 'takeovers/lunarNewYearTakeover/lunarNewYearDetails'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { SITE_TAKEOVER_CAMPAIGN_TAG_OVERRIDES } from '../siteTakeoverCampaignTagOverrides'
import type { SiteTakeoverCampaignTagLabelProps } from './siteTakeoverCampaignTagLabel.types'

export const LABEL_HEIGHT = rem(24)

const IconWrapper = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const LabelElement = styled.span`
  box-sizing: border-box;
  height: ${LABEL_HEIGHT};
  max-width: fit-content;
  padding: 0 ${rem(8)};
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-wrap: nowrap;
  border: 1px transparent;
  border-radius: ${props => props.theme.borderRadius.S};
  gap: ${rem(4)};

  ${IconWrapper} ${StyledSvgIcon} {
    ${setSvgIconSize(16)}
  }

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(8)};
  }

  /*
  TODO: Add more template variants here
  &.kind-primary {

  } */
`

const LabelText = styled(Caption)`
  font-size: ${rem(11)};
  font-weight: 500;
  line-height: ${rem(14)};

  ${mediaQueryUp.desktop} {
    font-size: ${rem(12)};
    line-height: ${rem(16)};
  }
`

type Kind = 'custom'

interface Props extends SiteTakeoverCampaignTagLabelProps {
  openCampaignModal?: () => void
}

export const SiteTakeoverCampaignTagLabelCore = React.forwardRef<HTMLSpanElement, Props>((props, ref) => {
  const {
    className,
    format,
    campaignTagId,
    forcedVisible,
    suppressWhenUSPVisible = false,
    onOfferCard = false,
    onOfferPage = false,
    openCampaignModal,
    offer,
    ...rest
  } = props

  const siteTakeoverTheme = useSiteTakeoverTheme()
  const theme = useTheme()
  const showModal = useContext(ModalContext)
  const {
    shouldShowTag,
    tagPayload,
    shouldShowModal,
  } = useSiteTakeoverCampaignUIState({
    campaignTagId,
    forceTagVisible: forcedVisible,
    suppressTagWhenUSPVisible: suppressWhenUSPVisible,
    onOfferCard,
  })

  const shouldShowIcon = useLabFeatureFlag(LabFeatureFlags.enableTakeoverModal)?.enabled
  const isLunarNewYear = siteTakeoverTheme?.id === LUNAR_NEW_YEAR_THEME_ID
  const shouldShowLunarIcon = onOfferPage && isLunarNewYear
  const campaignTagOverride = tagPayload?.campaignId ? SITE_TAKEOVER_CAMPAIGN_TAG_OVERRIDES[tagPayload.campaignId] : undefined
  const shouldShowOverrideIcon = campaignTagOverride?.shouldShowIcon?.({ onOfferPage }) ?? false
  const shouldShowAnyIcon = shouldShowOverrideIcon || shouldShowIcon || shouldShowLunarIcon
  const overrideIconColour = shouldShowOverrideIcon && campaignTagOverride?.iconColour === 'static-white'
    ? theme.palette.neutral.surface.staticWhite
    : undefined
  const handleModalOpen = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (shouldShowOverrideIcon && campaignTagOverride?.renderModal) {
      showModal(campaignTagOverride.renderModal({ offer, onOfferPage }))
      return
    }
    if (shouldShowLunarIcon) {
      showModal(<LunarSiteTakeoverDetailsModal />)
      return
    }
    if (!shouldShowModal) {
      return
    }
    openCampaignModal?.()
  }, [shouldShowOverrideIcon, campaignTagOverride, shouldShowModal, shouldShowLunarIcon, showModal, openCampaignModal, offer, onOfferPage])

  if (!shouldShowTag || !tagPayload?.campaignTag) {
    return null
  }

  const kind: Kind = 'custom'
  const paletteType = siteTakeoverTheme?.labelPalette ?? siteTakeoverTheme?.palette

  return <Pane paletteType={paletteType} background="none">
    <LabelElement
      {...rest}
      ref={ref}
      className={clsx(
        className,
        `kind-${kind}`,
      )}
      style={{
        background: siteTakeoverTheme?.labelStyle,
      }}
    >
      <LabelText variant="medium" format={format}>
        {campaignTagOverride ? campaignTagOverride.renderLabel() : tagPayload.campaignTag}
      </LabelText>
      {shouldShowAnyIcon && <IconWrapper onClick={handleModalOpen} style={{ color: overrideIconColour }}><LineInfoCircleIcon /></IconWrapper>}
    </LabelElement>
  </Pane>
})
